import React from "react";
import { Card } from "@/components/ui/card";
import { useI18n } from "@/contexts/I18nContext";
import {
  PenLine,
  Code2,
  BarChart3,
  Image as ImageIcon,
  FileText,
  Lightbulb,
  Sparkles,
} from "lucide-react";

// Each preset routes the user to the brain best suited for the task.
// provider values must match backend chat /send `provider` field.
const PRESETS = [
  { id: "write",      icon: PenLine,    provider: "openai",    accent: "from-sky-500/15" },
  { id: "code",       icon: Code2,      provider: "anthropic", accent: "from-violet-500/15" },
  { id: "analyze",    icon: BarChart3,  provider: "anthropic", accent: "from-emerald-500/15" },
  { id: "brainstorm", icon: Lightbulb,  provider: "openai",    accent: "from-amber-500/15" },
  { id: "image",      icon: ImageIcon,  provider: "gemini",    accent: "from-pink-500/15" },
  { id: "research",   icon: FileText,   provider: "gemini",    accent: "from-cyan-500/15" },
];

const PROVIDER_BADGE = {
  anthropic: { label: "Claude",  color: "text-orange-400 bg-orange-500/10" },
  openai:    { label: "GPT",     color: "text-emerald-300 bg-emerald-500/10" },
  gemini:    { label: "Gemini",  color: "text-sky-300 bg-sky-500/10" },
};

export function SmartBrainChips({ onPick }) {
  const { t } = useI18n();
  return (
    <div data-testid="smart-brain-chips">
      <div className="mb-3 flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
        <Sparkles className="h-3 w-3 text-[#9ACD32]" />
        {t("chat.smart_chips_title")}
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {PRESETS.map((p) => {
          const Icon = p.icon;
          const badge = PROVIDER_BADGE[p.provider];
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onPick({
                provider: p.provider,
                prompt: t(`chat.smart_chip_${p.id}_prompt`),
              })}
              className={`group relative overflow-hidden rounded-2xl border border-slate-800 bg-gradient-to-br ${p.accent} to-transparent p-4 text-left transition-all hover:border-[#9ACD32]/40 hover:translate-y-[-2px] hover:shadow-lg hover:shadow-[#9ACD32]/10`}
              data-testid={`smart-chip-${p.id}`}
            >
              <div className="flex items-start justify-between">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#0A0C1F] text-[#9ACD32]">
                  <Icon className="h-4 w-4" />
                </span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${badge.color}`}>
                  {badge.label}
                </span>
              </div>
              <p className="mt-3 text-sm font-bold text-white">{t(`chat.smart_chip_${p.id}_title`)}</p>
              <p className="mt-1 text-xs text-slate-400">{t(`chat.smart_chip_${p.id}_desc`)}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Keyword → recommended provider mapping for soft auto-routing.
// Patterns are intentionally simple — we want HIGH PRECISION (no false positives that nag users), not recall.
const ROUTING_RULES = [
  {
    provider: "anthropic",
    reasonKey: "chat.brain_hint_code",
    // Code / debug / programming languages
    test: (s) =>
      /\b(function|def\s|class\s|import\s|export\s|const\s|let\s|var\s|=>|console\.log|print\(|TypeError|NullPointerException|stack trace|debug|refactor|compile|stacktrace|<\/?[a-z]+>)\b/i.test(s) ||
      /\b(python|javascript|typescript|java|kotlin|swift|rust|golang|sql|regex|bash|shell|docker|kubernetes|react|next\.js|fastapi|spring boot)\b/i.test(s) ||
      /```/.test(s),
  },
  {
    provider: "anthropic",
    reasonKey: "chat.brain_hint_analyze",
    test: (s) =>
      /\b(analyze|analyse|breakdown|reasoning|step by step|pros and cons|compare|evaluate|legal|contract|policy|regulation|gdpr|compliance|audit|risk assessment)\b/i.test(s) ||
      /\b(analizza|ragionamento|passo per passo|pro e contro|valuta|confronta|contratto|conformit|rischi)\b/i.test(s) ||
      /\b(analiz|krok po kroku|porównaj|oceń|umowa|zgodność|ryzyko)\b/i.test(s),
  },
  {
    provider: "gemini",
    reasonKey: "chat.brain_hint_research",
    test: (s) =>
      /\b(research|sources|citations|state of the art|latest|recent news|literature review|long document|summarize this pdf|summarize this article)\b/i.test(s) ||
      /\b(ricerca|fonti|citazioni|stato dell'arte|ultime notizie|riassumi)\b/i.test(s) ||
      /\b(badania|źródła|cytaty|najnowsze|streszczenie)\b/i.test(s),
  },
  {
    provider: "openai",
    reasonKey: "chat.brain_hint_creative",
    test: (s) =>
      /\b(write a|draft an?|email|cover letter|landing page copy|tagline|slogan|tweet|caption|story|poem|marketing|persuasive)\b/i.test(s) ||
      /\b(scrivi|redigi|email|lettera|copywriting|titolo|slogan|tweet|didascalia|storia|marketing)\b/i.test(s) ||
      /\b(napisz|napiszesz|email|list|nagłówek|hasło|wpis|opowiadanie|marketing)\b/i.test(s),
  },
];

/**
 * Return a brain suggestion when the user's draft strongly matches another
 * provider than the currently selected one. Returns null when no clear match
 * or when the active provider already matches.
 */
export function suggestBrain(input, currentProvider) {
  if (!input || input.length < 12) return null;
  for (const rule of ROUTING_RULES) {
    if (rule.test(input)) {
      if (rule.provider !== currentProvider) {
        return {
          provider: rule.provider,
          providerLabel: PROVIDER_BADGE[rule.provider].label,
          reasonKey: rule.reasonKey,
        };
      }
      return null; // match but already on the right brain
    }
  }
  return null;
}
